import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { showError, showSuccess } from '../utils/alerts';
import { API_BASE_URL } from '../config';
import '../styles/pages/CargaCompras.css';

const meses = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const ComprobantePago = ({ token, id_company }) => {
  const [year, setYear] = useState('');
  const [provider, setProvider] = useState('');
  const [files, setFiles] = useState(Array(12).fill(null));
  const [status, setStatus] = useState(Array(12).fill({ cargado: false, procesado: false }));
  const [loadingConsulta, setLoadingConsulta] = useState(Array(12).fill(false));
  // meses bloqueados por el proveedor contrario (número de mes, 1-based)
  const [mesesConflicto, setMesesConflicto] = useState([]);

  const isSiiTotales = provider === 'sii_totales';

  // Fetch status cuando cambia año o proveedor
  useEffect(() => {
    if (!year || !token || !provider) return;

    const fetchStatus = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/estado-comprobantes`, {
          params: { id_company, anual: year, tipo_medio_pago: provider },
          headers: { Authorization: `Bearer ${token}` }
        });

        const newStatus = Array(12).fill({ cargado: false, procesado: false });
        response.data.forEach(row => {
          const idx = row.mes - 1;
          newStatus[idx] = { cargado: row.cargado, procesado: row.procesado };
        });
        setStatus(newStatus);
      } catch (error) {
        console.error('Error obteniendo estado de comprobantes', error);
      }
    };

    const fetchConflicto = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/estado-comprobantes-conflicto`, {
          params: { id_company, anual: year, tipo_medio_pago: provider },
          headers: { Authorization: `Bearer ${token}` }
        });
        setMesesConflicto(response.data); // array de números de mes (1-based)
      } catch (error) {
        console.error('Error obteniendo conflictos de comprobantes', error);
      }
    };

    fetchStatus();
    fetchConflicto();
  }, [year, provider, id_company, token]);

  const handleFileChange = (index, file) => {
    const newFiles = [...files];
    newFiles[index] = file;
    setFiles(newFiles);
  };

  const handleUpload = async (index) => {
    const file = files[index];
    if (!file || !year || !provider) {
      showError('Por favor selecciona un archivo, año y proveedor');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('id_company', id_company);
    formData.append('mes', index + 1);
    formData.append('anual', year);
    formData.append('bank', provider);

    try {
      await axios.post(`${API_BASE_URL}/api/cargar/cargaComprobanteRoutes`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      showSuccess(`Archivo de ${meses[index]} cargado exitosamente`);

      const newStatus = [...status];
      newStatus[index] = { cargado: true, procesado: false };
      setStatus(newStatus);
    } catch (error) {
      console.error('Error al cargar el archivo', error);
      showError(error.response?.data?.error || 'Error al cargar el archivo');
    }
  };

  const procesarComprobantes = async () => {
    if (!year) {
      showError('Selecciona un año');
      return;
    }

    try {
      await axios.post(
        `${API_BASE_URL}/api/procesar-ventas-comprobante`,
        { id_company, anual: year },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      showSuccess('Registros de comprobantes procesados correctamente');

      const updated = status.map(s => ({ ...s, procesado: s.cargado ? true : s.procesado }));
      setStatus(updated);
    } catch (error) {
      console.error('Error al procesar comprobantes', error);
      showError('Error al procesar los comprobantes');
    }
  };

  const consultarProveedor = async (index) => {
    if (!year || !provider) {
      showError('Por favor selecciona un año y proveedor');
      return;
    }

    const newLoading = [...loadingConsulta];
    newLoading[index] = true;
    setLoadingConsulta(newLoading);

    const endpoints = {
      sumup: '/api/consultar-sumup',
      getnet: '/api/consultar-getnet',
      tuu: '/api/consultar-tuu',
      mercadopago: '/api/consultar-mercadopago',
      sii_totales: '/api/consultar-sii-totales'
    };

    try {
      const response = await axios.post(
        `${API_BASE_URL}${endpoints[provider]}`,
        { id_company, year: parseInt(year), month: index + 1 },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 120000
        }
      );

      if (response.data.success) {
        const label = isSiiTotales ? 'SII Totales' : provider.toUpperCase();
        showSuccess(`${meses[index]}: Datos obtenidos y procesados de ${label}`);

        setStatus(prevStatus => {
          const updated = [...prevStatus];
          updated[index] = { cargado: true, procesado: true };
          return updated;
        });

        // Actualizar meses en conflicto: el mes recién procesado ahora bloquea al proveedor contrario
        setMesesConflicto(prev => {
          const mes = index + 1;
          if (!prev.includes(mes)) return [...prev, mes];
          return prev;
        });
      } else {
        showError(response.data.error || 'Error en la consulta');
      }
    } catch (error) {
      console.error('Error al consultar proveedor:', error);
      const errorMsg = error.response?.data?.error || 'Error al consultar el proveedor';
      showError(errorMsg);
    } finally {
      setLoadingConsulta(prev => {
        const reset = [...prev];
        reset[index] = false;
        return reset;
      });
    }
  };

  const getConsultarButtonText = (index) => {
    if (!provider) return 'Selecciona proveedor';
    if (loadingConsulta[index]) return 'Consultando...';
    if (isSiiTotales) return 'Consultar SII';
    return `Consultar ${provider.toUpperCase()}`;
  };

  const isConsultarDisabled = (index) => {
    if (!provider || loadingConsulta[index]) return true;
    if (mesesConflicto.includes(index + 1)) return true;
    if (status[index].procesado) return true;
    return false;
  };

  const getConsultarTitle = (index) => {
    if (mesesConflicto.includes(index + 1)) {
      if (isSiiTotales) return 'Este mes ya fue consultado con otro medio de pago';
      return 'Este mes ya fue consultado con SII Totales';
    }
    return '';
  };

  const mesConsultando = loadingConsulta.findIndex(v => v);
  const labelConsultando = isSiiTotales ? 'SII TOTALES' : provider.toUpperCase();

  return (
    <div className="carga-compras">
      {loadingConsulta.some(v => v) && (
        <div className="sii-loading-overlay">
          <div className="sii-loading-modal">
            <div className="sii-loading-spinner"></div>
            <h3>Consultando {labelConsultando}</h3>
            {mesConsultando >= 0 && <p>{meses[mesConsultando]}</p>}
            <small>Por favor espere, no cierre esta ventana</small>
          </div>
        </div>
      )}
      <h1>Carga de Comprobantes de Pago</h1>

      {/* Selector de Proveedor */}
      <div style={{ marginBottom: '20px' }}>
        <label htmlFor="providerSelect">Selecciona un Proveedor:</label>
        <select
          id="providerSelect"
          value={provider}
          onChange={(e) => setProvider(e.target.value)}
          style={{ marginLeft: '10px', padding: '5px' }}
        >
          <option value="">-- Seleccionar Medio de Pago --</option>
          <option value="sumup">Sumup</option>
          <option value="getnet">Getnet</option>
          <option value="tuu">TUU</option>
          <option value="mercadopago">Mercado Pago</option>
          <option value="sii_totales">SII Totales</option>
        </select>
      </div>

      {/* Selector de Año */}
      <div style={{ marginBottom: '20px' }}>
        <label htmlFor="yearSelect">Selecciona un Año:</label>
        <select
          id="yearSelect"
          value={year}
          onChange={(e) => setYear(e.target.value)}
          style={{ marginLeft: '10px', padding: '5px' }}
        >
          <option value="">Selecciona un año</option>
          {[...Array(5)].map((_, i) => (
            <option key={i} value={2023 + i}>
              {2023 + i}
            </option>
          ))}
        </select>
      </div>

      {/* Tabla de 12 meses */}
      <table>
        <thead>
          <tr>
            <th>Mes</th>
            {!isSiiTotales && <th>Archivo</th>}
            {!isSiiTotales && <th>Acción</th>}
            <th>Cargado</th>
            <th>Procesado</th>
            <th>Consultar Proveedor</th>
          </tr>
        </thead>
        <tbody>
          {meses.map((mes, index) => (
            <tr key={mes}>
              <td>{mes}</td>
              {!isSiiTotales && (
                <td>
                  <input
                    type="file"
                    accept=".csv,.xls,.xlsx"
                    onChange={(e) => handleFileChange(index, e.target.files[0])}
                  />
                </td>
              )}
              {!isSiiTotales && (
                <td>
                  <button
                    onClick={() => handleUpload(index)}
                    disabled={!provider}
                  >
                    Cargar
                  </button>
                </td>
              )}
              <td className={status[index].cargado ? 'status-true' : ''}>
                {status[index].cargado ? 'Sí' : 'No'}
              </td>
              <td className={status[index].procesado ? 'status-true' : ''}>
                {status[index].procesado ? 'Sí' : 'No'}
              </td>
              <td>
                <button
                  onClick={() => consultarProveedor(index)}
                  disabled={isConsultarDisabled(index)}
                  title={getConsultarTitle(index)}
                >
                  {getConsultarButtonText(index)}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Botón de Procesar (solo para proveedores con carga de archivo) */}
      {!isSiiTotales && (
        <button
          onClick={procesarComprobantes}
          style={{ marginTop: '20px', padding: '10px 20px' }}
        >
          Procesar Comprobantes
        </button>
      )}
    </div>
  );
};

export default ComprobantePago;
